import React, { FC, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import HeaderToc from "~/components/modules/qaFixedHeader/HeaderToc";
import QuestionButton from "~/components/modules/qaFixedHeader/QuestionButton";
import { useMounted } from "~/hooks/useMounted";
import { QuestionAnswer } from "~/models/questionAnswer";
import { Topic } from "~/models/topic";

export interface QaFixedHeaderProps {
  filteredQuestionContent: string;
  increaseFeedbackHelpfulCallback?: () => void;
  increaseFeedbackHelpfulUrl?: string;
  isForceSP?: boolean;
  lineShareUrl: string;
  questionAnswer: QuestionAnswer;
  topic: Topic;
  twitterShareUrl: string;
}

const QaFixedHeader: FC<QaFixedHeaderProps> = ({
  increaseFeedbackHelpfulCallback,
  filteredQuestionContent,
  isForceSP,
  lineShareUrl,
  questionAnswer,
  topic,
  twitterShareUrl,
}) => {
  const isMounted = useMounted();
  const [isHeaderShown, setIsHeaderShown] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const qaTitle = document.getElementById("qa-title");

    if (!qaTitle) {
      return;
    }

    const options: IntersectionObserverInit = {
      rootMargin: "0px",
      threshold: 1,
    };

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const entry = entries[0];
        const { isIntersecting } = entry;

        if (isMounted) {
          setIsHeaderShown(!isIntersecting);
        }
      },
      options
    );

    observer.observe(qaTitle);
  }, [isMounted]);

  return (
    <Container
      className={isHeaderShown ? "active" : undefined}
      isForceSP={isForceSP}
    >
      <QaFixedHeaderInner className="content-full-wrap">
        <QaFixedHeaderTitle>
          <QaFixedHeaderTitleLink href="#page-top" className="limited-line--2">
            {filteredQuestionContent}
          </QaFixedHeaderTitleLink>
        </QaFixedHeaderTitle>
        <HeaderToc
          increaseFeedbackHelpfulCallback={increaseFeedbackHelpfulCallback}
          isHeaderShown={isHeaderShown}
          lineShareUrl={lineShareUrl}
          questionAnswer={questionAnswer}
          twitterShareUrl={twitterShareUrl}
        />
        <QuestionButton topic={topic} />
      </QaFixedHeaderInner>
    </Container>
  );
};

export const QaFixedHeaderContainerBase = styled.div<{ isForceSP?: boolean }>`
  background-color: var(--body-bg-clr);
  border-bottom: 1px solid var(--sep-outline-bdr-clr);
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  transform: translateY(-100%);
  z-index: 11;
`;

const Container = styled(QaFixedHeaderContainerBase)`
  transition: transform 0.1s;
  &.active {
    transition: transform 0.15s;
    transform: translateY(0);
  }

  ${({ isForceSP }) => {
    if (isForceSP) {
      return css`
        @media only screen and (min-width: 769px) {
          max-width: 414px;
          margin: 0 auto;
        }
      `;
    } else {
      return css`
        @media only screen and (min-width: 992px) {
          display: none;
        }
      `;
    }
  }};
`;

export const QaFixedHeaderInner = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  height: 56px;
  justify-content: space-between;
  padding: 16px;
`;

export const QaFixedHeaderTitle = styled.div`
  flex: 1;
`;

export const QaFixedHeaderTitleLink = styled.a`
  color: var(--body-clr);
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
`;

export default QaFixedHeader;
